import { PostHogErrorBoundary, PostHogProvider } from '@posthog/react';
import posthog from 'posthog-js';
import React, { lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { HashRouter } from 'react-router-dom';
import App from '@/src/App';
import Fallback from '@/src/components/atoms/Fallback';
import {
  POSTHOG_API_HOST,
  POSTHOG_API_KEY,
  POSTHOG_ENABLED_URLS,
} from '@/src/services/analyticsService';

// Import self-hosted fonts
import '@fontsource/russo-one';
import '@fontsource/lekton/400.css';
import '@fontsource/lekton/700.css';
import '@fontsource/oswald/400.css';
import '@fontsource/oswald/700.css';
import '@fontsource/lato/400.css';
import '@fontsource/lato/700.css';
import '@fontsource/bangers';
import '@/src/fonts.css';
import ErrorBoundary from './src/components/organisms/ErrorBoundary';
import ButtonSoundProvider from './src/components/providers/ButtonSoundProvider';
import { AudioProvider } from './src/contexts/AudioContext';
import { BackgroundImageProvider } from './src/contexts/BackgroundImageContext';
import { TitleMusicProvider } from './src/contexts/TitleMusicContext';
import { store } from './src/store';

const ResponsiveGameWrapper = lazy(
  () => import('./src/components/templates/ResponsiveGameWrapper'),
);

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Could not find root element to mount to');
}

// Only initialize PostHog on production URLs
const isPostHogEnabled = POSTHOG_ENABLED_URLS.some((url) =>
  window.location.host.includes(url),
);

const client = isPostHogEnabled
  ? posthog.init(POSTHOG_API_KEY, { api_host: POSTHOG_API_HOST })
  : null;

const root = ReactDOM.createRoot(rootElement);

const AppWithProviders = (
  <HashRouter>
    <Provider store={store}>
      <AudioProvider>
        <TitleMusicProvider>
          <ButtonSoundProvider>
            <BackgroundImageProvider>
              <ResponsiveGameWrapper>
                <ErrorBoundary fallbackMessage="Something went wrong">
                  <App />
                </ErrorBoundary>
              </ResponsiveGameWrapper>
            </BackgroundImageProvider>
          </ButtonSoundProvider>
        </TitleMusicProvider>
      </AudioProvider>
    </Provider>
  </HashRouter>
);

// Conditionally wrap with PostHogProvider only when enabled
const AppWithPosthog = isPostHogEnabled ? (
  <PostHogProvider client={client}>
    <PostHogErrorBoundary fallback={<Fallback />}>
      {AppWithProviders}
    </PostHogErrorBoundary>
  </PostHogProvider>
) : (
  AppWithProviders
);

root.render(<React.StrictMode>{AppWithPosthog}</React.StrictMode>);
